import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useUnsavedChanges } from "./UnsavedChangesContext";

function todayYMD() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontWeight: 700 }}>{label}</div>
      {children}
    </div>
  );
}
function lastNameKey(name = "") {
  const parts = name.trim().toLowerCase().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : parts[0];
}

function formatPhone(value) {
  const raw = (value || "").toString().trim();
  if (!raw) return "";

  const parsed = parsePhoneNumberFromString(raw, "US");
  if (!parsed) return raw;
  if (!parsed.isValid()) return raw;
  return parsed.formatInternational();
}

function normalizePhone(value) {
  const raw = (value || "").toString().trim();
  if (!raw) return "";
  const parsed = parsePhoneNumberFromString(raw, "US");
  if (parsed && parsed.isValid()) return parsed.number;
  const digits = raw.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
}

function renderActivityText(label) {
  const text = (label || "").toString();
  if (!text.includes("/")) return text;
  const parts = text.split("/");
  return parts.map((part, idx) => (
    <React.Fragment key={`${part}-${idx}`}>
      {idx > 0 ? "/\u200b" : ""}
      {part.trimStart()}
    </React.Fragment>
  ));
}

// Mountain Time timezone helpers
function getMountainOffset(year, month, day) {
  // Returns timezone offset in hours (-6 for MDT, -7 for MST)
  // Uses Intl API to auto-detect based on date (handles DST)
  const testDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Denver",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const mtString = formatter.format(testDate);
  const mtHour = parseInt(mtString.split(", ")[1].split(":")[0], 10);
  // UTC 12:00 → MT 05:00 = UTC-7 (MST), → MT 06:00 = UTC-6 (MDT)
  return mtHour === 5 ? -7 : -6;
}

function parseArrivalDateTime(isoString) {
  // Parse ISO datetime string into { date, time }
  if (!isoString) return { date: "", time: "00:00" };
  const [datePart, timePart] = isoString.split("T");
  const time = timePart ? timePart.slice(0, 5) : "00:00";
  return { date: datePart || "", time };
}

function combineDateTime(dateStr, timeStr) {
  // Combine date and time into ISO datetime with Mountain Time offset
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  const offset = getMountainOffset(year, month, day);
  const sign = offset <= 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const offsetStr = `${sign}${String(absOffset).padStart(2, "0")}:00`;
  return `${dateStr}T${timeStr || "00:00"}:00${offsetStr}`;
}

export default function GuestsPage({ isAdmin }) {
  const { setUnsavedChanges, requestConfirm } = useUnsavedChanges();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [guestList, setGuestList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [families, setFamilies] = useState([]);

  const [selectedId, setSelectedId] = useState("");
  const [guest, setGuest] = useState(null);
  const originalGuestRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newGuestName, setNewGuestName] = useState("");
  const [newArrivalDate, setNewArrivalDate] = useState("");
  const [newArrivalTime, setNewArrivalTime] = useState("00:00");
  const [newDepartureDate, setNewDepartureDate] = useState("");
  const [newFamilyIds, setNewFamilyIds] = useState([]);
  const [newGuestVip, setNewGuestVip] = useState(false);
  const [creating, setCreating] = useState(false);

  const [itinerary, setItinerary] = useState(null);
  const [loadingItin, setLoadingItin] = useState(false);
  const [choicesData, setChoicesData] = useState(null);
  const [loadingChoices, setLoadingChoices] = useState(false);
  const [activeAddKey, setActiveAddKey] = useState("");
  const [fromDate, setFromDate] = useState(todayYMD());
  const [toDate, setToDate] = useState("");
  const selectedRowRef = useRef(null);
const sortedGuestList = useMemo(() => {
  return [...guestList].sort((a, b) => {
    const la = lastNameKey(a.name);
    const lb = lastNameKey(b.name);
    if (la !== lb) return la.localeCompare(lb);
    return (a.name || "").localeCompare(b.name || "");
  });
}, [guestList]);

  useEffect(() => {
    if (!guest) {
      setUnsavedChanges(false, null);
      return;
    }
    const snapshot = (g) =>
      JSON.stringify({
        name: g.name || "",
        email: g.email || "",
        phone: g.phone || "",
        level: g.level || "",
        arrivalDate: g.arrivalDate || "",
        departureDate: g.departureDate || "",
        vip: !!g.vip,
        familyIds: Array.isArray(g.familyIds) ? [...g.familyIds].sort() : [],
      });
    const original = originalGuestRef.current ? snapshot(originalGuestRef.current) : "";
    const current = snapshot(guest);
    const nextDirty = original && original !== current;
    setUnsavedChanges(nextDirty, saveGuest);
  }, [guest]);

  // used to refresh after add/remove
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("guestId");
    if (id) setSelectedId(id);
  }, [location.search]);

  useEffect(() => {
    if (!selectedId) return;
    if (loadingList) return;
    if (!selectedRowRef.current) return;
    const t = setTimeout(() => {
      selectedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => clearTimeout(t);
  }, [selectedId, loadingList, guestList]);

  // list guests (debounced)
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setLoadingList(true);
        const qs = new URLSearchParams();
qs.set("search", search.trim());

if (fromDate) qs.set("from", fromDate);
if (toDate) qs.set("to", toDate);

const res = await fetch(`/api/guests?${qs.toString()}`);

        const json = await res.json();
        if (!cancelled) setGuestList(Array.isArray(json) ? json : []);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
}, [search, fromDate, toDate]);


  // load selected guest
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!selectedId) {
        setGuest(null);
        setItinerary(null);
        setChoicesData(null);
        return;
      }
      const res = await fetch(`/api/guests/${selectedId}`);
      const json = await res.json();
      if (!cancelled) {
        const nextGuest = {
          ...json,
          vip: !!json.vip,
          phone: formatPhone(json.phone || ""),
          familyIds: Array.isArray(json.familyIds) ? json.familyIds : [],
          families: Array.isArray(json.families) ? json.families : [],
        };
        setGuest(nextGuest);
        originalGuestRef.current = nextGuest ? { ...nextGuest } : null;
        setUnsavedChanges(false, null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    let cancelled = false;
    async function loadFamilies() {
      try {
        const res = await fetch("/api/families");
        const json = await res.json();
        if (!cancelled) setFamilies(Array.isArray(json) ? json : []);
      } catch {
        if (!cancelled) setFamilies([]);
      }
    }
    loadFamilies();
    return () => { cancelled = true; };
  }, []);

  // load itinerary
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!selectedId) return;
      setLoadingItin(true);
      try {
        const res = await fetch(`/api/guests/${selectedId}/itinerary`);
        const json = await res.json();
        if (!cancelled) setItinerary(json);
      } finally {
        if (!cancelled) setLoadingItin(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedId, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    async function loadChoices() {
      if (!selectedId) return;
      setLoadingChoices(true);
      try {
        const res = await fetch(`/api/guests/${selectedId}/choices`);
        const json = await res.json();
        if (!cancelled) setChoicesData(res.ok ? json : null);
      } finally {
        if (!cancelled) setLoadingChoices(false);
      }
    }
    loadChoices();
    return () => { cancelled = true; };
  }, [selectedId]);

  const scheduledActivityIds = useMemo(() => {
    const set = new Set();
    (itinerary?.days || []).forEach((day) => {
      const amIds = day?.am?.activityIds || [];
      const pmIds = day?.pm?.activityIds || [];
      amIds.forEach((id) => set.add(id));
      pmIds.forEach((id) => set.add(id));
    });
    return set;
  }, [itinerary]);

  const choiceActivityMap = useMemo(() => {
    const map = {};
    (choicesData?.activities || []).forEach((a) => {
      map[a.id] = a;
    });
    return map;
  }, [choicesData]);

  function triggerRefresh() {
    setRefreshKey((x) => x + 1);
  }

  async function copyGuestLink() {
    if (!guest?.id) return;
    const url = `${window.location.origin}/guest/${guest.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus("Copied");
    } catch {
      setCopyStatus("Copy failed");
    }
    window.setTimeout(() => setCopyStatus(""), 2000);
  }

  async function copyGuestChoicesLink() {
    if (!guest?.id) return;
    const url = `${window.location.origin}/guest/${guest.id}/choices`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus("Choices link copied");
    } catch {
      setCopyStatus("Copy failed");
    }
    window.setTimeout(() => setCopyStatus(""), 2000);
  }

  function copyFamilyLink(familyId) {
    if (!familyId) return;
    const url = `${window.location.origin}/family/${familyId}`;
    navigator.clipboard.writeText(url).then(
      () => setCopyStatus("Family link copied"),
      () => setCopyStatus("Copy failed")
    );
    window.setTimeout(() => setCopyStatus(""), 2000);
  }

  function copyFamilyChoicesLink(familyId) {
    if (!familyId) return;
    const url = `${window.location.origin}/family/${familyId}/choices`;
    navigator.clipboard.writeText(url).then(
      () => setCopyStatus("Family choices link copied"),
      () => setCopyStatus("Copy failed")
    );
    window.setTimeout(() => setCopyStatus(""), 2000);
  }

  async function createFamily(name) {
    const trimmed = (name || "").trim();
    if (!trimmed) return null;

    const res = await fetch("/api/families", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: trimmed }),
    });
    const json = await res.json();
    if (!res.ok) {
      alert(json?.error || json?.details || "Failed to create family");
      return null;
    }
    const newFamily = { id: json.id, name: json.name || trimmed, vip: false };
    setFamilies((prev) => [...prev, newFamily]);
    return newFamily;
  }

  async function updateFamilyVip(familyId, vip) {
    if (!isAdmin || !familyId) return;
    const res = await fetch(`/api/families/${familyId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vip: !!vip }),
    });
    const json = await res.json();
    if (!res.ok) {
      alert(json?.error || json?.details || "Failed to update family VIP");
      return;
    }
    setFamilies((prev) =>
      prev.map((f) => (f.id === familyId ? { ...f, vip: !!vip } : f))
    );
    setGuest((prev) =>
      prev
        ? {
            ...prev,
            families: (prev.families || []).map((f) =>
              f.id === familyId ? { ...f, vip: !!vip } : f
            ),
          }
        : prev
    );
  }

  async function saveGuest() {
    if (!isAdmin) return;
    if (!guest?.name) {
      alert("Name is required");
      return false;
    }
    setSaving(true);
    try {
      const uniqueFamilyIds = Array.from(new Set((guest.familyIds || []).filter(Boolean)));
      const res = await fetch(`/api/guests/${guest.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: guest.name,
          email: guest.email,
          phone: normalizePhone(guest.phone),
          level: guest.level,
          arrivalDate: guest.arrivalDate,
          departureDate: guest.departureDate,
          vip: !!guest.vip,
          familyIds: uniqueFamilyIds,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        alert(json?.error || "Failed to save");
        return false;
      }
      const nextFamilyIds = uniqueFamilyIds;
      const familyMap = new Map((families || []).map((f) => [f.id, f]));
      setGuest((prev) => {
        if (!prev) return prev;
        const nextGuest = {
          ...prev,
          familyIds: nextFamilyIds,
          families: nextFamilyIds.map((id) => {
            const family = familyMap.get(id);
            return { id, name: family?.name || "", vip: !!family?.vip };
          }),
        };
        originalGuestRef.current = { ...nextGuest };
        setUnsavedChanges(false, null);
        return nextGuest;
      });
      triggerRefresh();
      return true;
    } finally {
      setSaving(false);
    }
  }

  async function createGuest() {
    if (!isAdmin) return;
    const name = newGuestName.trim();
    if (!name) return;

    if (newArrivalDate && newDepartureDate && newDepartureDate < newArrivalDate) {
      alert("Departure date must be after arrival date.");
      return;
    }

    setCreating(true);
    const arrivalToSend = newArrivalDate ? combineDateTime(newArrivalDate, newArrivalTime) : null;
    const departureToSend = newDepartureDate || newArrivalDate || null;
    const uniqueFamilyIds = Array.from(new Set((newFamilyIds || []).filter(Boolean)));
    const res = await fetch(`/api/guests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        email: "",
        phone: normalizePhone(""),
        level: "N/A",
        arrivalDate: arrivalToSend,
        departureDate: departureToSend,
        vip: !!newGuestVip,
        familyIds: uniqueFamilyIds,
      }),
    });
    const json = await res.json();
  if (!res.ok) {
  alert(json?.error || json?.details || `Failed (${res.status})`);
  setCreating(false);
  return;
}

    setSelectedId(json.id);
    triggerRefresh();
    setNewGuestName("");
    setNewArrivalDate("");
    setNewArrivalTime("00:00");
    setNewDepartureDate("");
    setNewFamilyIds([]);
    setNewGuestVip(false);
    setCreateOpen(false);
    setCreating(false);
  }

  async function removeFromSchedule(scheduleId) {
    if (!isAdmin) return;
    if (!selectedId) return;
    const res = await fetch(`/api/schedule/${scheduleId}/remove-guest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guestId: selectedId }),
    });
    const json = await res.json();
    if (!res.ok) {
      alert(json?.error || "Failed to remove");
      return;
    }
    setItinerary((prev) => {
      if (!prev) return prev;
      const days = (prev.days || []).map((day) => {
        let nextDay = day;
        ["am", "pm"].forEach((key) => {
          if (day?.[key]?.scheduleId === scheduleId) {
            nextDay = {
              ...nextDay,
              [key]: { scheduleId: null, activity: "No Activity Scheduled", activityIds: [] },
            };
          }
        });
        return nextDay;
      });
      return { ...prev, days };
    });
  }

  async function addToSchedule(date, slot, scheduleId, option) {
    if (!isAdmin) return;
    if (!selectedId) return;
    const res = await fetch(`/api/schedule/${scheduleId}/add-guest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guestId: selectedId }),
    });
    const json = await res.json();
    if (!res.ok) {
      alert(json?.error || "Failed to add");
      return;
    }
    const slotKey = slot?.toLowerCase() === "pm" ? "pm" : "am";
    const nextCell = {
      scheduleId,
      activity: option?.activity || "Activity scheduled",
      activityIds: option?.activityIds || [],
    };
    setItinerary((prev) => {
      if (!prev) return prev;
      const days = (prev.days || []).map((day) =>
        day.date === date ? { ...day, [slotKey]: nextCell } : day
      );
      return { ...prev, days };
    });
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }}>
      {/* LEFT: guest list */}
      <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 12 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            placeholder="Search guests…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, padding: 10, borderRadius: 10, border: "1px solid #ccc" }}
          />
          <button onClick={() => setCreateOpen(true)} disabled={!isAdmin}>+ Add</button>
        </div>
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Arrival Date</div>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: "75%", padding: 8, borderRadius: 10, border: "1px solid #ccc" }}
            />
          </div>

          <div>
            <div style={{ fontWeight: 700, fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Departure Date</div>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: "75%", padding: 8, borderRadius: 10, border: "1px solid #ccc" }}
            />
          </div>
        </div>

        {(fromDate || toDate) && (
          <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Showing guests whose stay overlaps this window
            </div>
            <button
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
              style={{ fontSize: 12 }}
            >
              Clear
            </button>
          </div>
        )}

        <div style={{ marginTop: 12, maxHeight: 600, overflowY: "auto" }}>
          {loadingList && <div style={{ opacity: 0.7 }}>Loading…</div>}
          {!loadingList &&
            sortedGuestList.map((g) => (
              <div
                key={g.id}
                ref={selectedId === g.id ? selectedRowRef : null}
                onClick={() =>
                  requestConfirm(() => {
                    setSelectedId(g.id);
                  })
                }
                style={{
                  padding: "10px 10px",
                  borderRadius: 10,
                  cursor: "pointer",
                  background: selectedId === g.id ? "var(--selection-bg)" : "transparent",
                }}
              >
                {g.name}
              </div>
            ))}
        </div>
      </div>

      {createOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => !creating && setCreateOpen(false)}
        >
          <div
            style={{
              background: "var(--card)",
              borderRadius: 12,
              padding: 16,
              width: "100%",
              maxWidth: 360,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              display: "grid",
              gap: 12,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: 16 }}>Add Guest</div>
            <input
              autoFocus
              placeholder="Guest name"
              value={newGuestName}
              onChange={(e) => setNewGuestName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createGuest();
              }}
              style={{ padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
            />
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700 }}>Arrival Date & Time</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <input
                  type="date"
                  value={newArrivalDate}
                  onChange={(e) => setNewArrivalDate(e.target.value)}
                  onBlur={() => {
                    if (!newDepartureDate && newArrivalDate) {
                      setNewDepartureDate(newArrivalDate);
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
                />
                <input
                  type="time"
                  value={newArrivalTime}
                  onChange={(e) => setNewArrivalTime(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
                />
              </div>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700 }}>Departure Date</label>
              <input
                type="date"
                value={newDepartureDate}
                onChange={(e) => setNewDepartureDate(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                style={{ padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
              />
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700 }}>Families/Groups</label>
              <FamilyPicker
                families={families}
                selectedIds={newFamilyIds}
                onChange={setNewFamilyIds}
                onCreate={createFamily}
                disabled={!isAdmin}
              />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700 }}>
              <input
                type="checkbox"
                checked={newGuestVip}
                onChange={(e) => setNewGuestVip(e.target.checked)}
                disabled={!isAdmin}
              />
              VIP Guest
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </button>
              <button onClick={createGuest} disabled={creating || !newGuestName.trim()}>
                {creating ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RIGHT: guest detail + itinerary */}
      <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 16 }}>
        {!guest ? (
          <div style={{ opacity: 0.7 }}>Select a guest to view/edit details.</div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0 }}>{guest.name}</h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {copyStatus && <span style={{ fontSize: 12, opacity: 0.7 }}>{copyStatus}</span>}
                <button onClick={saveGuest} disabled={!isAdmin || saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Name">
                <input
                  value={guest.name}
                  onChange={(e) => setGuest({ ...guest, name: e.target.value })}
                  disabled={!isAdmin}
                />
              </Field>

              <Field label="Email">
                <input
                  value={guest.email}
                  onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                  disabled={!isAdmin}
                />
              </Field>

              <Field label="VIP Guest">
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!guest.vip}
                    onChange={(e) => setGuest({ ...guest, vip: e.target.checked })}
                    disabled={!isAdmin}
                  />
                  <span style={{ fontSize: 12, opacity: 0.8 }}>Only schedule with their family/group</span>
                </label>
              </Field>

              <Field label="Phone">
                <input
                  value={guest.phone || ""}
                  onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
                  onBlur={(e) => setGuest({ ...guest, phone: formatPhone(e.target.value) })}
                  disabled={!isAdmin}
                  placeholder="(555) 123-4567"
                />
              </Field>

              <Field label="Horseback Riding Level">
                <select
                  value={guest.level || ""}
                  onChange={(e) => setGuest({ ...guest, level: e.target.value })}
                  disabled={!isAdmin}
                >
                  <option value="">Select level…</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </Field>

              <Field label="Arrival Date & Time">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input
                    type="date"
                    value={parseArrivalDateTime(guest.arrivalDate).date}
                    onChange={(e) => {
                      const { time } = parseArrivalDateTime(guest.arrivalDate);
                      setGuest({ ...guest, arrivalDate: combineDateTime(e.target.value, time) });
                    }}
                    disabled={!isAdmin}
                  />
                  <input
                    type="time"
                    value={parseArrivalDateTime(guest.arrivalDate).time}
                    onChange={(e) => {
                      const { date } = parseArrivalDateTime(guest.arrivalDate);
                      setGuest({ ...guest, arrivalDate: combineDateTime(date, e.target.value) });
                    }}
                    disabled={!isAdmin}
                  />
                </div>
              </Field>

              <Field label="Departure Date">
                <input
                  type="date"
                  value={guest.departureDate ? guest.departureDate.slice(0, 10) : ""}
                  onChange={(e) => setGuest({ ...guest, departureDate: e.target.value })}
                  disabled={!isAdmin}
                />
              </Field>

              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Families/Groups">
                  <FamilyPicker
                    families={families}
                    selectedIds={guest.familyIds || []}
                    onChange={(ids) => setGuest({ ...guest, familyIds: ids })}
                    onCreate={createFamily}
                    onSave={saveGuest}
                    saveDisabled={!isAdmin || saving}
                    resetKey={guest.id}
                    disabled={!isAdmin}
                  />
                  {!!guest.families?.length && (
                    <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                      <div style={{ display: "grid", gap: 8 }}>
                        {guest.families.map((f) => (
                          <div
                            key={f.id}
                            style={{ display: "grid", gap: 6 }}
                          >
                            <div style={{ fontWeight: 700 }}>{f.name || "(Unnamed family)"}</div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                gap: 8,
                                alignItems: "stretch",
                              }}
                            >
                              <button
                                onClick={() => copyFamilyLink(f.id)}
                                style={{ fontSize: 12, minHeight: 30, height: "100%", width: "100%", whiteSpace: "normal" }}
                              >
                                Copy {f.name || "Family/Group"} Itinerary Link
                              </button>
                              <button
                                onClick={() => copyFamilyChoicesLink(f.id)}
                                style={{ fontSize: 12, minHeight: 30, height: "100%", width: "100%", whiteSpace: "normal" }}
                              >
                                Copy {f.name || "Family/Group"} Activity Selection Link
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Field>
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>Itinerary</h3>
                <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
                  <button onClick={copyGuestLink} style={{ fontSize: 12, minHeight: 30, height: "100%", whiteSpace: "nowrap" }}>
                    Copy Individual Itinerary Link
                  </button>
                  <button onClick={copyGuestChoicesLink} style={{ fontSize: 12, minHeight: 30, height: "100%", whiteSpace: "nowrap" }}>
                    Copy Activity Selection Link
                  </button>
                  {copyStatus && <span style={{ fontSize: 12, opacity: 0.7 }}>{copyStatus}</span>}
                </div>
              </div>

              <details style={{ marginBottom: 12 }}>
                <summary style={{ cursor: "pointer", fontWeight: 700 }}>
                  Requested activities (1-10)
                </summary>
                {loadingChoices && <div style={{ marginTop: 8 }}>Loading choices…</div>}
                {!loadingChoices && (
                  <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                    {(choicesData?.skipItems || [])
                      .map((item, idx) => ({ item, idx }))
                      .filter(({ item }) => item?.label)
                      .map(({ item, idx }) => (
                        <label
                          key={`${item.label}-${idx}`}
                          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, opacity: 0.7 }}
                        >
                          <input
                            type="checkbox"
                            checked={!!choicesData?.skipSelections?.[idx]}
                            readOnly
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    {Array.from({ length: 10 }, (_, idx) => {
                      const choiceId = choicesData?.choices?.[idx] || "";
                      const activityName = choiceId ? (choiceActivityMap[choiceId]?.name || "Activity") : "Not selected";
                      const scheduled = choiceId ? scheduledActivityIds.has(choiceId) : false;
                      return (
                        <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ fontWeight: 700 }}>#{idx + 1}</span>
                          <span>{activityName}</span>
                          {choiceId ? (
                            <span style={{ fontSize: 12, opacity: 0.7 }}>
                              {scheduled ? "Scheduled" : "Not scheduled"}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </details>

              {loadingItin && <div>Loading itinerary…</div>}

              {!loadingItin && itinerary && itinerary.days?.length === 0 && (
                <div style={{ opacity: 0.7 }}>
                  No scheduled days (check Arrival/Departure dates). Departure day has no activities.
                </div>
              )}

              {!loadingItin && itinerary && itinerary.days?.length > 0 && (
                <ItineraryTable
                  guestId={guest.id}
                  days={itinerary.days}
                  onRemove={removeFromSchedule}
                  onAdd={addToSchedule}
                  isAdmin={isAdmin}
                  activeAddKey={activeAddKey}
                  setActiveAddKey={setActiveAddKey}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ItineraryTable({ guestId, days, onRemove, onAdd, isAdmin, activeAddKey, setActiveAddKey }) {
  return (
    <div
      style={{
        border: "1px solid var(--stroke)",
        borderRadius: 12,
        overflow: "visible",
        background: "var(--card)",
        boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr", fontWeight: 800, background: "var(--table-header-bg)" }}>
        <div style={{ padding: 10 }}>Date</div>
        <div style={{ padding: 10 }}>AM</div>
        <div style={{ padding: 10 }}>PM</div>
      </div>

      {days.map((d) => (
        <div key={d.date} style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr", borderTop: "1px solid var(--stroke)" }}>
          <div style={{ padding: 10, fontWeight: 700 }}>{d.date}</div>

          <SlotCell
            guestId={guestId}
            date={d.date}
            slot="AM"
            cell={d.am}
            onRemove={onRemove}
            onAdd={onAdd}
            isAdmin={isAdmin}
            activeAddKey={activeAddKey}
            setActiveAddKey={setActiveAddKey}
          />

          <SlotCell
            guestId={guestId}
            date={d.date}
            slot="PM"
            cell={d.pm}
            onRemove={onRemove}
            onAdd={onAdd}
            isAdmin={isAdmin}
            activeAddKey={activeAddKey}
            setActiveAddKey={setActiveAddKey}
          />
        </div>
      ))}
    </div>
  );
}

function SlotCell({ guestId, date, slot, cell, onRemove, onAdd, isAdmin, activeAddKey, setActiveAddKey }) {
  // If slot doesn't exist (arrival AM is null), show dash.
  if (cell === null) {
    return <div style={{ padding: 10, opacity: 0.5 }}>—</div>;
  }

  // cell shape: { scheduleId, activity } OR { scheduleId:null, activity:"No Activity Scheduled" }
  const hasSchedule = !!cell.scheduleId;
  const [isAddOpen, setIsAddOpen] = useState(false);
  const key = `${date}:${slot}`;
  const addLocked = activeAddKey && activeAddKey !== key;
  const buttonStyle = addLocked ? { opacity: 0.4, pointerEvents: "none" } : {};

  return (
    <div style={{ padding: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}>
        <div>
          <div className="activity-text">
            {hasSchedule ? renderActivityText(cell.activity) : isAddOpen ? "" : renderActivityText(cell.activity)}
          </div>
          {hasSchedule && cell.startTime ? (
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>{cell.startTime}</div>
          ) : null}
        </div>

        {hasSchedule ? (
          isAdmin ? (
            <button
              onClick={() => onRemove(cell.scheduleId)}
              style={{ fontSize: 12, ...buttonStyle }}
            >
              Remove
            </button>
          ) : null
        ) : (
          <AddToSlot
            guestId={guestId}
            date={date}
            slot={slot}
            onAdd={onAdd}
            isAdmin={isAdmin}
            onOpenChange={setIsAddOpen}
            activeAddKey={activeAddKey}
            setActiveAddKey={setActiveAddKey}
          />
        )}
      </div>
    </div>
  );
}

function AddToSlot({ guestId, date, slot, onAdd, isAdmin, onOpenChange, activeAddKey, setActiveAddKey }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const panelRef = useRef(null);
  const key = `${date}:${slot}`;
  const addLocked = activeAddKey && activeAddKey !== key;

  async function loadOptions() {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/schedule/available?date=${encodeURIComponent(date)}&slot=${encodeURIComponent(slot)}&guestId=${encodeURIComponent(guestId)}`
      );
      const json = await res.json();
      setOptions(Array.isArray(json) ? json : []);
    } finally {
      setLoading(false);
    }
  }

  async function toggle() {
    if (!open) await loadOptions();
    const next = !open;
    setOpen(next);
    onOpenChange?.(next);
    setActiveAddKey?.(next ? key : "");
  }

  async function add() {
    if (!selected) return;
    const option = options.find((o) => o.scheduleId === selected);
    await onAdd(date, slot, selected, option);
    setOpen(false);
    onOpenChange?.(false);
    setActiveAddKey?.("");
    setSelected("");
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={toggle}
        style={{ fontSize: 12, ...(addLocked ? { opacity: 0.4, pointerEvents: "none" } : {}) }}
        disabled={!isAdmin}
      >
        {open ? "Cancel" : "Add"}
      </button>

      {open && isAdmin && (
        <div
          ref={panelRef}
          style={{
            position: "absolute",
            right: 0,
            bottom: "100%",
            marginBottom: 8,
            zIndex: 10,
            background: "var(--card)",
            border: "1px solid #d5dcdd",
            borderRadius: 10,
            padding: 10,
            boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
            display: "grid",
            gap: 8,
            minWidth: 280,
            maxWidth: 340,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          <select value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">Select activity…</option>
            {options.map((o) => (
              <option key={o.scheduleId} value={o.scheduleId}>
                {o.activity} (rem: {o.remaining})
              </option>
            ))}
          </select>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={add} disabled={!selected || loading}>
              Save
            </button>
            {loading && <span style={{ opacity: 0.6 }}>Loading…</span>}
          </div>

          {!loading && options.length === 0 && (
            <span style={{ opacity: 0.7, lineHeight: 1.3, whiteSpace: "normal" }}>
              No activities with space
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function FamilyPicker({ families, selectedIds, onChange, onCreate, onSave, saveDisabled, resetKey, disabled }) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [dirty, setDirty] = useState(false);

  const normalized = query.trim().toLowerCase();
  const selectedSet = new Set(selectedIds || []);
  const selectedItems = families.filter((f) => selectedSet.has(f.id));
  const filtered = families.filter((f) =>
    (f.name || "").toLowerCase().includes(normalized)
  );
  const maxResults = 10;
  const limited = filtered.slice(0, maxResults);
  const exactMatch = families.some((f) => (f.name || "").toLowerCase() === normalized);

  function toggleFamily(id) {
    const set = new Set(selectedIds || []);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    setDirty(true);
    onChange(Array.from(set));
  }

  async function addFamily() {
    if (!normalized || exactMatch || !onCreate) return;
    setCreating(true);
    const created = await onCreate(query);
    if (created) {
      onChange([...(selectedIds || []), created.id]);
      setQuery("");
    }
    setCreating(false);
  }

  async function saveSelection() {
    if (!onSave || saveDisabled) return;
    await onSave();
    setDirty(false);
  }

  useEffect(() => {
    setDirty(false);
  }, [resetKey]);

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {selectedItems.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {selectedItems.map((f) => (
            <span
              key={f.id}
              style={{
                padding: "2px 8px",
                borderRadius: 999,
                background: "var(--sand)",
                fontSize: 12,
              }}
            >
              {f.name || "(Unnamed family)"}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          placeholder="Search families…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          style={{ flex: 1, padding: 8, borderRadius: 8, border: "1px solid #ccc" }}
        />
        {dirty ? (
          <button onClick={saveSelection} disabled={saveDisabled || disabled}>
            {saveDisabled ? "Saving…" : "Save"}
          </button>
        ) : (
          <button
            onClick={addFamily}
            disabled={disabled || creating || !normalized || exactMatch}
          >
            {creating ? "Adding…" : "Add"}
          </button>
        )}
      </div>

      {!normalized && (
        <div style={{ fontSize: 12, opacity: 0.7 }}>Type at least 2 letters to search.</div>
      )}

      {normalized.length > 0 && normalized.length < 2 && (
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          Keep typing to filter families.
        </div>
      )}

      {normalized.length >= 2 && filtered.length === 0 && (
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          No matching families. Click Add to create.
        </div>
      )}

      {normalized.length >= 2 && limited.length > 0 && (
        <div style={{ display: "grid", gap: 6 }}>
          {limited.map((f) => {
            const selected = (selectedIds || []).includes(f.id);
            return (
              <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleFamily(f.id)}
                  disabled={disabled}
                />
                <span>{f.name || "(Unnamed family)"}</span>
              </label>
            );
          })}
          {filtered.length > maxResults && (
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Showing {maxResults} of {filtered.length} matches. Refine search.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
